import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { AiFeature } from '../../models/AiFeature'
import { RootState } from '../store'
import { Scorecard, ScorecardResult } from '../../models/Scorecard'
import { RoomEvent } from '../media/media'
import { selectExecutedAiFeatures } from './executedAiFeatures'
import { hasTenMinutesPassed } from '../utils'

interface ClientProfileState {
  clientProfileAiFeatures: EntityState<AiFeature, string>
  clientProfileAiFeaturesLoading: boolean
  clientProfileScorecards: EntityState<Scorecard, string>
  clientProfileScorecardsLoading: boolean
  clientProfileResults: EntityState<ScorecardResult, string>
  clientProfileResultsPollCounter: number
  clientProfileResultPollingEnabled: boolean
  clientProfileEventsForPolling: RoomEvent[]
  clientProfileEventsForPollingLoading: boolean
  clientProfileEventsPollingEnabled: boolean
}

const clientProfileAiFeaturesAdapter = createEntityAdapter<AiFeature>()
const clientProfileScorecardsAdapter = createEntityAdapter<Scorecard>()
const clientProfileResultsAdapter = createEntityAdapter<ScorecardResult>()

const initialState: ClientProfileState = {
  clientProfileAiFeatures: clientProfileAiFeaturesAdapter.getInitialState(),
  clientProfileAiFeaturesLoading: true,
  clientProfileScorecards: clientProfileScorecardsAdapter.getInitialState(),
  clientProfileScorecardsLoading: true,
  clientProfileResults: clientProfileResultsAdapter.getInitialState(),
  clientProfileResultsPollCounter: 0,
  clientProfileResultPollingEnabled: false,
  clientProfileEventsForPolling: [],
  clientProfileEventsForPollingLoading: true,
  clientProfileEventsPollingEnabled: false,
}

export const clientProfileSlice = createSlice({
  name: 'clientProfile',
  initialState,
  reducers: {
    setClientProfileAiFeaturesLoading: (state, action: PayloadAction<boolean>) => {
      state.clientProfileAiFeaturesLoading = action.payload
    },
    setClientProfileEventsForPollingLoading: (state, action: PayloadAction<boolean>) => {
      state.clientProfileEventsForPollingLoading = action.payload
    },
    setClientProfileScorecardsLoading: (state, action: PayloadAction<boolean>) => {
      state.clientProfileScorecardsLoading = action.payload
    },
    addClientProfileResultsPollCounter: (state) => {
      state.clientProfileResultsPollCounter++
    },
    startClientProfileEventsPolling: (state) => {
      state.clientProfileEventsPollingEnabled = true
    },
    stopClientProfileEventsPolling: (state) => {
      state.clientProfileEventsPollingEnabled = false
    },
    startClientProfileResultsPolling: (state) => {
      state.clientProfileResultPollingEnabled = true
    },
    stopClientProfileResultsPolling: (state) => {
      state.clientProfileResultPollingEnabled = false
    },
    setClientProfileAiFeatures: (state, action: PayloadAction<AiFeature[]>) => {
      clientProfileAiFeaturesAdapter.setAll(state.clientProfileAiFeatures, action.payload)
    },
    setClientProfileScorecards: (state, action: PayloadAction<Scorecard[]>) => {
      clientProfileScorecardsAdapter.setAll(state.clientProfileScorecards, action.payload)
    },
    setClientProfileResults: (state, action: PayloadAction<ScorecardResult[]>) => {
      clientProfileResultsAdapter.setAll(state.clientProfileResults, action.payload)
    },
    setClientProfileEventsForPolling: (state, action: PayloadAction<RoomEvent[]>) => {
      state.clientProfileEventsForPolling = action.payload
    },
  },
})

export const {
  setClientProfileAiFeaturesLoading,
  setClientProfileEventsForPollingLoading,
  setClientProfileScorecardsLoading,
  addClientProfileResultsPollCounter,
  startClientProfileEventsPolling,
  stopClientProfileEventsPolling,
  setClientProfileAiFeatures,
  setClientProfileScorecards,
  setClientProfileResults,
  setClientProfileEventsForPolling,
  startClientProfileResultsPolling,
  stopClientProfileResultsPolling,
} = clientProfileSlice.actions

const clientProfileAiFeaturesSelectors = clientProfileAiFeaturesAdapter.getSelectors(
  (state: RootState) => state.meetingSummary.clientProfile.clientProfileAiFeatures,
)
const clientProfilesSelectors = clientProfileResultsAdapter.getSelectors((state: RootState) => state.meetingSummary.clientProfile.clientProfileResults)

const clientProfileScorecardsSelector = clientProfileScorecardsAdapter.getSelectors(
  (state: RootState) => state.meetingSummary.clientProfile.clientProfileScorecards,
)

export const selectClientProfileAiFeatures = (state: RootState) => clientProfileAiFeaturesSelectors.selectAll(state)

export const selectClientProfileLoading = (state: RootState) =>
  state.meetingSummary.clientProfile.clientProfileAiFeaturesLoading ||
  state.meetingSummary.clientProfile.clientProfileEventsForPollingLoading ||
  state.meetingSummary.clientProfile.clientProfileScorecardsLoading ||
  state.meetingSummary.clientProfile.clientProfileResultPollingEnabled

export const selectClientProfileEventsForPollingLoading = (state: RootState) => state.meetingSummary.clientProfile.clientProfileEventsForPollingLoading

export const selectClientProfileEventPollingEnabled = (state: RootState) => state.meetingSummary.clientProfile.clientProfileEventsPollingEnabled

export const selectClientProfileResultPollingEnabled = (state: RootState) => state.meetingSummary.clientProfile.clientProfileResultPollingEnabled

export const selectClientProfileResults = (state: RootState) => clientProfilesSelectors.selectAll(state)

export const selectClientProfileEventsForPolling = (state: RootState) => state.meetingSummary.clientProfile.clientProfileEventsForPolling

export const selectClientProfileResultsPollCounter = (state: RootState) => state.meetingSummary.clientProfile.clientProfileResultsPollCounter

export const selectClientProfileScorecards = (state: RootState) => clientProfileScorecardsSelector.selectAll(state)

export const selectClientProfileScorecardsLoading = (state: RootState) => state.meetingSummary.clientProfile.clientProfileScorecardsLoading

export const selectShouldPollClientProfileEvents = createSelector(
  [selectClientProfileEventsForPollingLoading, selectClientProfileEventsForPolling],
  (eventsForPollingLoading, eventsForPolling) => {
    if (eventsForPollingLoading) {
      return false
    }
    const aiFeatureExecutionFinished = eventsForPolling.some((e) => e.type === 'aiFeatureExecutionFinished')
    if (!aiFeatureExecutionFinished) {
      return true
    }
    const sortedEvents = [...eventsForPolling].sort((event1, event2) => {
      const { timestamp: timestamp1 } = event1
      const { timestamp: timestamp2 } = event2
      return timestamp1.localeCompare(timestamp2)
    })
    const latestEvent = sortedEvents.pop()
    return !latestEvent || latestEvent.type === 'userAllocationUpdated'
  },
)

const MAX_RESULT_POLL_COUNT = 30

export const selectShouldPollClientProfileResults = createSelector(
  [
    selectClientProfileResults,
    selectClientProfileEventsForPolling,
    selectClientProfileResultsPollCounter,
    selectExecutedAiFeatures,
    selectClientProfileAiFeatures,
  ],
  (clientProfileResults, eventsForPolling, resultsPollCounter, executedAiFeatures, clientProfileAiFeatures) => {
    const executedAiFeaturesIds = executedAiFeatures.map((f) => f.id)
    if (!clientProfileAiFeatures.some((f) => executedAiFeaturesIds.includes(f.id))) {
      return false
    }
    if (resultsPollCounter > MAX_RESULT_POLL_COUNT) {
      return false
    }
    if (!eventsForPolling.length) {
      return false
    }
    if (clientProfileResults.length > 0) {
      return false
    }
    const latestAiFeatureExecutionFinishedEvent = eventsForPolling
      .filter((e) => e.type === 'aiFeatureExecutionFinished')
      .reduce((prev: null | RoomEvent, current: RoomEvent) => (prev && prev.timestamp.localeCompare(current.timestamp) === 1 ? prev : current), null)
    if (!latestAiFeatureExecutionFinishedEvent) {
      return false
    }
    return !hasTenMinutesPassed(latestAiFeatureExecutionFinishedEvent?.timestamp)
  },
)
